(() => {
  'use strict';

  const TYPES = 'product,page,article';
  const LIMIT = 10;
  const INDEX_MIN = 4;

  const normalizeIdentifier = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const isCompact = (query) => !/\s/.test(String(query || '').trim());

  const root = () => document.querySelector('[data-pw-predictive-search]');

  const productUrl = (product) => product?.url || (product?.handle ? `/products/${product.handle}` : '#');

  const productPath = (product) => {
    try {
      return new URL(productUrl(product), window.location.origin).pathname.replace(/\/$/, '').toLowerCase();
    } catch (error) {
      return String(productUrl(product) || '').split('?')[0].replace(/\/$/, '').toLowerCase();
    }
  };

  const imageUrl = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  };

  const priceLabel = (product) => {
    const raw = product?.price ?? product?.price_min ?? product?.variants?.[0]?.price;
    if (raw === null || raw === undefined || raw === '') return '';
    const text = String(raw).trim();
    if (!text) return '';
    if (/[€$£]/.test(text) || /op aanvraag/i.test(text)) return text;
    const numeric = Number(text.replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(numeric)) return text;
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(numeric);
  };

  const ensurePreviewOnSearchNavigation = (target) => {
    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (!previewThemeId || !target) return;

    if (target instanceof HTMLFormElement) {
      let field = target.querySelector('input[name="preview_theme_id"]');
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'preview_theme_id';
        target.append(field);
      }
      field.value = previewThemeId;
      return;
    }

    if (target instanceof HTMLAnchorElement) {
      try {
        const url = new URL(target.href, window.location.origin);
        if (url.origin !== window.location.origin || url.pathname.replace(/\/$/, '') !== '/search') return;
        url.searchParams.set('preview_theme_id', previewThemeId);
        target.href = url.toString();
      } catch (error) {
        // Laat de originele link staan.
      }
    }
  };

  const predictiveEndpoint = () => {
    const configured = root()?.dataset.predictiveSearchUrl || '/search/suggest';
    return configured.endsWith('.json') ? configured : `${configured}.json`;
  };

  const fetchPredictive = async (query) => {
    const url = new URL(predictiveEndpoint(), window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(LIMIT));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set(
      'resources[options][fields]',
      'title,vendor,product_type,tag,variants.title,variants.sku,variants.barcode'
    );

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
    const payload = await response.json();
    return payload?.resources?.results?.products || [];
  };

  const fetchIndexPage = async (baseUrl, page) => {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('page', String(page));
    url.searchParams.set('_pw_search_refresh', `${Date.now()}-${page}`);

    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error(`Search index failed: ${response.status}`);
    const payload = await response.json();
    if (!payload || !Array.isArray(payload.products)) throw new Error('Search index returned invalid data');
    return payload;
  };

  const fetchCatalog = async () => {
    const baseUrl = root()?.dataset.searchIndexUrl || '/collections/all?view=pw-search-index';
    const firstPage = await fetchIndexPage(baseUrl, 1);
    const pages = Math.max(1, Number(firstPage.pages || 1));
    const requests = [];
    for (let page = 2; page <= pages; page += 1) requests.push(fetchIndexPage(baseUrl, page));
    const rest = await Promise.all(requests);
    return [firstPage, ...rest].flatMap((page) => page.products || []);
  };

  const bestScore = (values, needle, offset) => {
    let best = Number.POSITIVE_INFINITY;
    values.forEach((value) => {
      const haystack = normalizeIdentifier(value);
      if (!haystack) return;
      if (haystack === needle) best = Math.min(best, offset);
      else if (haystack.startsWith(needle)) best = Math.min(best, offset + 100 + haystack.length - needle.length);
      else {
        const index = haystack.indexOf(needle);
        if (index !== -1) best = Math.min(best, offset + 200 + index);
      }
    });
    return best;
  };

  const catalogMatches = (items, query) => {
    const needle = normalizeIdentifier(query);
    if (!needle) return [];

    return items
      .filter((product) => !product.hidden)
      .map((product) => {
        const identifierScore = bestScore([...(product.skus || []), ...(product.barcodes || [])], needle, 0);
        const titleScore = bestScore([product.title], needle, 1000);
        const score = Math.min(identifierScore, titleScore);
        return Number.isFinite(score)
          ? { ...product, image: product.image ? { url: product.image } : null, _searchScore: score }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => a._searchScore - b._searchScore || String(a.title || '').localeCompare(String(b.title || ''), 'nl'));
  };

  const mergeProducts = (groups) => {
    const seen = new Set();
    const merged = [];

    groups.flat().forEach((product) => {
      if (!product || product.hidden) return;
      const tags = Array.isArray(product.tags) ? product.tags.map((tag) => String(tag).toLowerCase()) : [];
      if (tags.includes('eol') || tags.includes('product archief')) return;

      const key = productPath(product) || String(product.id || product.title || '').toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push(product);
    });

    return merged;
  };

  const enrichPredictive = (products, catalog) => {
    const byPath = new Map(catalog.filter((product) => !product.hidden).map((product) => [productPath(product), product]));
    return products.map((product) => {
      const match = byPath.get(productPath(product));
      if (!match) return product;
      return {
        ...product,
        price: product.price ?? match.price,
        compareAtPrice: product.compareAtPrice ?? match.compareAtPrice,
        image: product.image || product.featured_image || (match.image ? { url: match.image } : null),
        vendor: product.vendor || match.vendor
      };
    });
  };

  const createCard = (product) => {
    const card = document.createElement('article');
    card.className = 'pw-search-product-card';

    const link = document.createElement('a');
    link.href = productUrl(product);

    const media = document.createElement('span');
    media.className = 'pw-search-product-card__media';
    const src = imageUrl(product.image || product.featured_image);
    if (src) {
      const image = document.createElement('img');
      image.src = src;
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'pw-search-product-card__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      media.append(placeholder);
    }

    const body = document.createElement('span');
    body.className = 'pw-search-product-card__body';

    if (product.vendor) {
      const vendor = document.createElement('span');
      vendor.className = 'pw-search-product-card__vendor';
      vendor.textContent = product.vendor;
      body.append(vendor);
    }

    const title = document.createElement('strong');
    title.className = 'pw-search-product-card__title';
    title.textContent = product.title || 'Product bekijken';
    body.append(title);

    const label = priceLabel(product);
    if (label) {
      const price = document.createElement('span');
      price.className = 'pw-search-product-card__price';
      price.append(document.createTextNode(label));
      body.append(price);
    }

    link.append(media, body);
    card.append(link);
    return card;
  };

  const supplementSubmittedSearch = async () => {
    if (window.location.pathname.replace(/\/$/, '') !== '/search') return;

    const params = new URLSearchParams(window.location.search);
    const query = String(params.get('q') || '').trim();
    if (!query || !isCompact(query) || normalizeIdentifier(query).length < INDEX_MIN) return;
    if (params.has('sort_by') || params.has('page') || [...params.keys()].some((key) => key.startsWith('filter.'))) return;

    const section = document.querySelector('[data-search-results-section]');
    const resultsGrid = section?.querySelector('[data-results-grid]');
    if (!(section instanceof Element) || !(resultsGrid instanceof Element)) return;

    let predictiveProducts = [];
    let catalog = [];

    try {
      predictiveProducts = await fetchPredictive(query);
    } catch (error) {
      // De catalogusfallback hieronder kan het resultaat alsnog leveren.
    }

    try {
      catalog = await fetchCatalog();
    } catch (error) {
      // Predictive search blijft als fallback beschikbaar.
    }

    const indexed = catalogMatches(catalog, query);
    const predictive = enrichPredictive(predictiveProducts, catalog);
    const matches = mergeProducts([indexed, predictive]);
    if (!matches.length) return;

    const existingPaths = new Set(
      [...resultsGrid.querySelectorAll('.pw-search-product-card > a[href]')].map((link) => {
        try {
          return new URL(link.href, window.location.origin).pathname.replace(/\/$/, '').toLowerCase();
        } catch (error) {
          return String(link.getAttribute('href') || '').split('?')[0].replace(/\/$/, '').toLowerCase();
        }
      })
    );

    const missing = matches.filter((product) => {
      const path = productPath(product);
      return path && !existingPaths.has(path);
    });
    if (!missing.length) return;

    let mixedGrid = resultsGrid.querySelector('.pw-search-mixed-grid');
    if (!mixedGrid) {
      const group = document.createElement('div');
      group.className = 'pw-search-results-group';
      mixedGrid = document.createElement('div');
      mixedGrid.className = 'pw-search-mixed-grid';
      group.append(mixedGrid);
      resultsGrid.replaceChildren(group);
    }

    missing.forEach((product) => mixedGrid.append(createCard(product)));
    section.classList.remove('is-quick-search');

    const count = resultsGrid.querySelectorAll('.pw-search-product-card, .pw-search-content-card').length;
    const countLabel = section.querySelector('.pw-results-toolbar__count');
    if (countLabel) countLabel.textContent = `${count} ${count === 1 ? 'resultaat' : 'resultaten'}`;
  };

  const bindPreviewPersistence = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      try {
        const action = new URL(form.action, window.location.origin);
        if (action.origin === window.location.origin && action.pathname.replace(/\/$/, '') === '/search') {
          ensurePreviewOnSearchNavigation(form);
        }
      } catch (error) {
        // Geen actie nodig.
      }
    }, true);

    document.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (link instanceof HTMLAnchorElement) ensurePreviewOnSearchNavigation(link);
    }, true);
  };

  const start = () => {
    bindPreviewPersistence();
    supplementSubmittedSearch();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
